class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();

    this.onActiveFilterClick =
      this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');

    if (facetForm) {
      facetForm.addEventListener(
        'input',
        this.debouncedOnSubmit.bind(this)
      );
    }

    const facetWrapper =
      this.querySelector('#FacetsWrapperDesktop');

    if (facetWrapper) {
      facetWrapper.addEventListener(
        'keyup',
        onKeyUpEscape
      );
    }
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state
        ? event.state.searchParams
        : FacetFiltersForm.searchParamsInitial;

      if (
        searchParams ===
        FacetFiltersForm.searchParamsPrev
      ) {
        return;
      }

      FacetFiltersForm.renderPage(
        searchParams,
        null,
        false
      );
    };

    window.addEventListener(
      'popstate',
      onHistoryChange
    );

    const initialize = () => {
      FacetFiltersForm.initializeMobileFacets();
      FacetFiltersForm.ensureMobileCategory();
      FacetFiltersForm.initializeMobileCategory();
    };

    if (
      document.readyState === 'loading'
    ) {
      document.addEventListener(
        'DOMContentLoaded',
        initialize,
        { once: true }
      );
    } else {
      initialize();
    }

    window.requestAnimationFrame(() => {
      initialize();
    });

    window.setTimeout(() => {
      initialize();
    }, 100);

    window.setTimeout(() => {
      initialize();
    }, 500);
  }

  static toggleActiveFacets(disable = true) {
    document
      .querySelectorAll('.js-facet-remove')
      .forEach((element) => {
        element.classList.toggle(
          'disabled',
          disable
        );
      });
  }

  static renderPage(
    searchParams,
    event,
    updateURLHash = true
  ) {
    FacetFiltersForm.searchParamsPrev =
      searchParams;

    const sections =
      FacetFiltersForm.getSections();

    const updateEvent =
      FacetFiltersForm.startUpdateEvent(
        searchParams
      );

    const countContainer =
      document.getElementById(
        'ProductCount'
      );

    const countContainerDesktop =
      document.getElementById(
        'ProductCountDesktop'
      );

    const loadingSpinners =
      document.querySelectorAll(
        '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
      );

    loadingSpinners.forEach(
      (spinner) => {
        spinner.classList.remove(
          'hidden'
        );
      }
    );

    const productGridContainer =
      document.getElementById(
        'ProductGridContainer'
      );

    if (productGridContainer) {
      const collection =
        productGridContainer.querySelector(
          '.collection'
        );

      if (collection) {
        collection.classList.add(
          'loading'
        );
      }
    }

    if (countContainer) {
      countContainer.classList.add(
        'loading'
      );
    }

    if (countContainerDesktop) {
      countContainerDesktop.classList.add(
        'loading'
      );
    }

    sections.forEach(
      (section) => {
        const url =
          `${window.location.pathname}?section_id=${section.section}&${searchParams}`;

        const filterDataUrl =
          (element) =>
            element.url === url;

        if (
          FacetFiltersForm.filterData.some(
            filterDataUrl
          )
        ) {
          FacetFiltersForm.renderSectionFromCache(
            filterDataUrl,
            event,
            updateEvent
          );
        } else {
          FacetFiltersForm.renderSectionFromFetch(
            url,
            event,
            updateEvent
          );
        }
      }
    );

    if (updateURLHash) {
      FacetFiltersForm.updateURLHash(
        searchParams
      );
    }
  }

  static startUpdateEvent(
    searchParams
  ) {
    const {
      SearchUpdateEvent,
      CollectionUpdateEvent
    } = window.StandardEvents || {};

    if (
      !SearchUpdateEvent &&
      !CollectionUpdateEvent
    ) {
      return;
    }

    const facetsForm =
      document.querySelector(
        'facet-filters-form'
      );

    const facetsContainer =
      document.querySelector(
        '.facets-container'
      );

    const collectionId =
      facetsContainer?.dataset.collectionId ||
      null;

    const collectionHandle =
      facetsContainer?.dataset.collectionHandle ||
      '';

    const currentCount =
      parseInt(
        document
          .getElementById('ProductCount')
          ?.dataset.productCount
      ) || 0;

    const urlSearchParams =
      new URLSearchParams(
        searchParams
      );

    const isSearchPage =
      facetsContainer?.dataset.template ===
      'search';

    const isCollectionPage =
      facetsContainer?.dataset.template ===
      'collection';

    const dispatchTarget =
      facetsForm || document;

    let deferred;

    if (isSearchPage) {
      deferred =
        SearchUpdateEvent.createPromise();

      dispatchTarget.dispatchEvent(
        new SearchUpdateEvent({
          search: {
            query:
              urlSearchParams.get('q') ||
              '',

            productFilters:
              SearchUpdateEvent.parseProductFilters(
                urlSearchParams
              ),

            sortKey:
              SearchUpdateEvent.getSortKey(
                urlSearchParams
              ),
          },

          promise:
            deferred.promise,
        })
      );
    } else if (isCollectionPage) {
      deferred =
        CollectionUpdateEvent.createPromise();

      dispatchTarget.dispatchEvent(
        new CollectionUpdateEvent({
          collection: {
            id: collectionId,
            handle: collectionHandle,
            productsCount: currentCount,
          },

          productFilters:
            CollectionUpdateEvent.parseProductFilters(
              urlSearchParams
            ),

          sortKey:
            CollectionUpdateEvent.getSortKey(
              urlSearchParams
            ),

          promise:
            deferred.promise,
        })
      );
    }

    if (!deferred) {
      return;
    }

    return {
      resolve: (filteredCount) => {
        deferred.resolve({
          [
            isSearchPage
              ? 'totalCount'
              : 'productsCount'
          ]: filteredCount,
        });
      },

      reject:
        deferred.reject,
    };
  }

  static renderSectionFromFetch(
    url,
    event,
    updateEvent
  ) {
    fetch(url)
      .then((response) =>
        response.text()
      )
      .then((html) => {
        FacetFiltersForm.filterData =
          [
            ...FacetFiltersForm.filterData,
            {
              html,
              url,
            },
          ];

        FacetFiltersForm.renderSection(
          html,
          event,
          updateEvent
        );
      })
      .catch((error) => {
        console.error(error);

        updateEvent?.reject(error);
      });
  }

  static renderSectionFromCache(
    filterDataUrl,
    event,
    updateEvent
  ) {
    const cached =
      FacetFiltersForm.filterData.find(
        filterDataUrl
      );

    if (!cached) {
      return;
    }

    FacetFiltersForm.renderSection(
      cached.html,
      event,
      updateEvent
    );
  }

  static renderSection(
    html,
    event,
    updateEvent
  ) {
    FacetFiltersForm.renderFilters(
      html,
      event
    );

    FacetFiltersForm.renderProductGridContainer(
      html
    );

    FacetFiltersForm.renderProductCount(
      html,
      updateEvent
    );

    FacetFiltersForm.initializeMobileFacets();

    FacetFiltersForm.renderMobileCategory(
      html
    );

    window.requestAnimationFrame(() => {
      FacetFiltersForm.initializeMobileFacets();
      FacetFiltersForm.ensureMobileCategory();
      FacetFiltersForm.initializeMobileCategory();
    });

    window.setTimeout(() => {
      FacetFiltersForm.ensureMobileCategory();
      FacetFiltersForm.initializeMobileCategory();
    }, 50);

    window.setTimeout(() => {
      FacetFiltersForm.ensureMobileCategory();
      FacetFiltersForm.initializeMobileCategory();
    }, 300);

    window.setTimeout(() => {
      FacetFiltersForm.ensureMobileCategory();
    }, 700);

    if (
      typeof initializeScrollAnimationTrigger ===
      'function'
    ) {
      initializeScrollAnimationTrigger(
        html.innerHTML
      );
    }
  }

  static initializeMobileFacets() {
    const mobileWrapper =
      document.querySelector(
        '.mobile-facets__wrapper'
      );

    if (!mobileWrapper) {
      return;
    }

    const mobileOpen =
      mobileWrapper.querySelector(
        '.mobile-facets__open'
      );

    if (mobileOpen) {
      mobileOpen.removeAttribute(
        'hidden'
      );
    }

    if (
      typeof mobileWrapper.bindEvents ===
      'function'
    ) {
      mobileWrapper.bindEvents();
    }

    if (
      window.matchMedia(
        '(max-width: 749px)'
      ).matches
    ) {
      mobileWrapper.style.removeProperty(
        'display'
      );

      mobileWrapper.style.removeProperty(
        'visibility'
      );

      if (mobileOpen) {
        mobileOpen.style.removeProperty(
          'display'
        );

        mobileOpen.style.removeProperty(
          'visibility'
        );
      }
    }
  }

  static getMobileCategorySelector() {
    return [
      '#MobileCategoryFacet',
      '.opal-mobile-category-facet',
      '.mobile-category-facet'
    ].join(',');
  }

  static getMobileCategoryFrom(
    root
  ) {
    if (!root) {
      return null;
    }

    return root.querySelector(
      FacetFiltersForm.getMobileCategorySelector()
    );
  }

  static renderMobileCategory(
    html
  ) {
    if (!html) {
      return;
    }

    const parsedHTML =
      new DOMParser().parseFromString(
        html,
        'text/html'
      );

    const sourceCategory =
      FacetFiltersForm.getMobileCategoryFrom(
        parsedHTML
      );

    const mobileWrapper =
      document.querySelector(
        '.mobile-facets__wrapper'
      );

    const mobileForm =
      document.getElementById(
        'FacetFiltersFormMobile'
      );

    if (
      !mobileWrapper ||
      !mobileForm
    ) {
      return;
    }

    if (!sourceCategory) {
      FacetFiltersForm.ensureMobileCategory();
      return;
    }

    const currentCategory =
      FacetFiltersForm.getMobileCategoryFrom(
        mobileForm
      );

    const newCategory =
      sourceCategory.cloneNode(
        true
      );

    newCategory.id =
      'MobileCategoryFacet';

    newCategory.classList.add(
      'mobile-category-facet'
    );

    newCategory.classList.add(
      'opal-mobile-category-facet'
    );

    newCategory.classList.remove(
      'js-filter'
    );

    newCategory.dataset.opalCategory =
      'true';

    newCategory.dataset.mobileCategory =
      'true';

    if (currentCategory) {
      currentCategory.replaceWith(
        newCategory
      );
    } else {
      const mobileMain =
        mobileForm.querySelector(
          '#FacetsWrapperMobile'
        );

      if (mobileMain) {
        mobileMain.insertBefore(
          newCategory,
          mobileMain.firstElementChild
        );
      }
    }

    FacetFiltersForm.initializeMobileCategory();
  }

  static ensureMobileCategory() {
    const mobileForm =
      document.getElementById(
        'FacetFiltersFormMobile'
      );

    const mobileMain =
      document.getElementById(
        'FacetsWrapperMobile'
      );

    if (
      !mobileForm ||
      !mobileMain
    ) {
      return;
    }

    let category =
      FacetFiltersForm.getMobileCategoryFrom(
        mobileMain
      );

    if (category) {
      category.id =
        'MobileCategoryFacet';

      category.classList.add(
        'mobile-category-facet'
      );

      category.classList.add(
        'opal-mobile-category-facet'
      );

      category.classList.remove(
        'js-filter'
      );

      category.dataset.opalCategory =
        'true';

      category.dataset.mobileCategory =
        'true';

      category.style.removeProperty(
        'display'
      );

      category.style.removeProperty(
        'visibility'
      );

      category.style.removeProperty(
        'opacity'
      );

      /*
      Always move Category to the
      first position.
      */
      if (
        mobileMain.firstElementChild !==
        category
      ) {
        mobileMain.insertBefore(
          category,
          mobileMain.firstElementChild
        );
      }

      FacetFiltersForm.initializeMobileCategory();

      return;
    }

    /*
    If the category disappeared after
    an AJAX update, retrieve it from
    the current document if available.
    */
    const existingCategory =
      document.querySelector(
        '#MobileCategoryFacet, .opal-mobile-category-facet, .mobile-category-facet'
      );

    if (
      existingCategory &&
      existingCategory !== category
    ) {
      existingCategory.classList.add(
        'mobile-category-facet'
      );

      existingCategory.classList.add(
        'opal-mobile-category-facet'
      );

      existingCategory.classList.remove(
        'js-filter'
      );

      existingCategory.dataset.opalCategory =
        'true';

      existingCategory.dataset.mobileCategory =
        'true';

      mobileMain.insertBefore(
        existingCategory,
        mobileMain.firstElementChild
      );

      FacetFiltersForm.initializeMobileCategory();

      return;
    }
  }

  static initializeMobileCategory() {
    const categories =
      document.querySelectorAll(
        '#MobileCategoryFacet, .opal-mobile-category-facet, .mobile-category-facet'
      );

    categories.forEach(
      (category) => {
        category.id =
          'MobileCategoryFacet';

        category.classList.add(
          'mobile-category-facet'
        );

        category.classList.add(
          'opal-mobile-category-facet'
        );

        category.classList.remove(
          'js-filter'
        );

        category.dataset.opalCategory =
          'true';

        category.dataset.mobileCategory =
          'true';

        if (
          category.dataset.opalCategoryInitialized ===
          'true'
        ) {
          return;
        }

        category.dataset.opalCategoryInitialized =
          'true';

        const summary =
          category.querySelector(
            ':scope > summary'
          );

        if (!summary) {
          return;
        }

        summary.addEventListener(
          'click',
          () => {
            window.setTimeout(() => {
              category.classList.toggle(
                'is-open',
                category.open
              );
            }, 0);
          }
        );

        category.addEventListener(
          'toggle',
          () => {
            category.classList.toggle(
              'is-open',
              category.open
            );
          }
        );

        const closeButton =
          category.querySelector(
            '.mobile-facets__close-button'
          );

        if (closeButton) {
          closeButton.addEventListener(
            'click',
            (event) => {
              event.preventDefault();
              category.removeAttribute(
                'open'
              );
              category.classList.remove(
                'is-open'
              );
            }
          );
        }
      }
    );
  }

  static renderProductGridContainer(
    html
  ) {
    const productGridContainer =
      document.getElementById(
        'ProductGridContainer'
      );

    if (!productGridContainer) {
      return;
    }

    const parsedHTML =
      new DOMParser().parseFromString(
        html,
        'text/html'
      );

    const newProductGridContainer =
      parsedHTML.getElementById(
        'ProductGridContainer'
      );

    if (!newProductGridContainer) {
      return;
    }

    productGridContainer.innerHTML =
      newProductGridContainer.innerHTML;

    productGridContainer
      .querySelectorAll(
        '.scroll-trigger'
      )
      .forEach((element) => {
        element.classList.add(
          'scroll-trigger--cancel'
        );
      });
  }

  static renderProductCount(
    html,
    updateEvent
  ) {
    const parsedHtml =
      new DOMParser().parseFromString(
        html,
        'text/html'
      );

    const sourceCount =
      parsedHtml.getElementById(
        'ProductCount'
      );

    if (!sourceCount) {
      return;
    }

    const count =
      sourceCount.innerHTML;

    const container =
      document.getElementById(
        'ProductCount'
      );

    const containerDesktop =
      document.getElementById(
        'ProductCountDesktop'
      );

    if (container) {
      container.innerHTML =
        count;

      container.dataset.productCount =
        sourceCount.dataset.productCount ||
        '';

      container.dataset.totalCount =
        sourceCount.dataset.totalCount ||
        '';

      container.classList.remove(
        'loading'
      );
    }

    if (containerDesktop) {
      containerDesktop.innerHTML =
        count;

      containerDesktop.classList.remove(
        'loading'
      );
    }

    const loadingSpinners =
      document.querySelectorAll(
        '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
      );

    loadingSpinners.forEach(
      (spinner) => {
        spinner.classList.add(
          'hidden'
        );
      }
    );

    updateEvent?.resolve(
      parseInt(
        sourceCount.dataset.productCount
      ) || 0
    );
  }

  static renderFilters(
    html,
    event
  ) {
    const parsedHTML =
      new DOMParser().parseFromString(
        html,
        'text/html'
      );

    const facetDetailsElementsFromFetch =
      parsedHTML.querySelectorAll(
        '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
      );

    const facetDetailsElementsFromDom =
      document.querySelectorAll(
        '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
      );

    /*
    IMPORTANT:
    Mobile Category does NOT have js-filter.
    Therefore it is never treated as a Shopify facet.
    */

    Array.from(
      facetDetailsElementsFromDom
    ).forEach(
      (currentElement) => {
        if (
          currentElement.matches(
            '#MobileCategoryFacet, .mobile-category-facet, .opal-mobile-category-facet'
          )
        ) {
          return;
        }

        if (
          !Array.from(
            facetDetailsElementsFromFetch
          ).some(
            ({ id }) =>
              currentElement.id ===
              id
          )
        ) {
          currentElement.remove();
        }
      }
    );

    const matchesId = (
      element
    ) => {
      const jsFilter =
        event?.target?.closest(
          '.js-filter'
        );

      return jsFilter
        ? element.id ===
            jsFilter.id
        : false;
    };

    const facetsToRender =
      Array.from(
        facetDetailsElementsFromFetch
      ).filter(
        (element) =>
          !matchesId(element)
      );

    const countsToRender =
      Array.from(
        facetDetailsElementsFromFetch
      ).find(matchesId);

    facetsToRender.forEach(
      (
        elementToRender,
        index
      ) => {
        const currentElement =
          document.getElementById(
            elementToRender.id
          );

        if (currentElement) {
          currentElement.innerHTML =
            elementToRender.innerHTML;
        } else {
          if (index > 0) {
            const {
              className:
                previousElementClassName,
              id:
                previousElementId,
            } =
              facetsToRender[
                index - 1
              ];

            if (
              elementToRender.className ===
              previousElementClassName
            ) {
              const previousElement =
                document.getElementById(
                  previousElementId
                );

              if (previousElement) {
                previousElement.after(
                  elementToRender
                );
              }

              return;
            }
          }

          if (
            elementToRender.parentElement
          ) {
            const parentId =
              elementToRender
                .parentElement.id;

            if (parentId) {
              const parentElement =
                document.querySelector(
                  `#${CSS.escape(
                    parentId
                  )}`
                );

              const firstFilter =
                parentElement?.querySelector(
                  '.js-filter'
                );

              if (firstFilter) {
                firstFilter.before(
                  elementToRender
                );
              }
            }
          }
        }
      }
    );

    FacetFiltersForm.renderMobileCategory(
      html
    );

    FacetFiltersForm.renderActiveFacets(
      parsedHTML
    );

    FacetFiltersForm.renderAdditionalElements(
      parsedHTML
    );

    window.requestAnimationFrame(
      () => {
        FacetFiltersForm.ensureMobileCategory();
      }
    );

    if (
      countsToRender &&
      event
    ) {
      const closestJSFilter =
        event.target.closest(
          '.js-filter'
        );

      if (!closestJSFilter) {
        return;
      }

      const closestJSFilterID =
        closestJSFilter.id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(
          countsToRender,
          document.getElementById(
            closestJSFilterID
          )
        );

        FacetFiltersForm.renderMobileCounts(
          countsToRender,
          document.getElementById(
            closestJSFilterID
          )
        );

        const newFacetDetailsElement =
          document.getElementById(
            closestJSFilterID
          );

        if (!newFacetDetailsElement) {
          return;
        }

        const isTextInput =
          event.target.getAttribute(
            'type'
          ) === 'text';

        if (!isTextInput) {
          const originatingInputId =
            event.target.id;

          const matchingInput =
            originatingInputId
              ? newFacetDetailsElement.querySelector(
                  `#${CSS.escape(
                    originatingInputId
                  )}`
                )
              : null;

          if (matchingInput) {
            matchingInput.focus();
          }
        }
      }
    }
  }

  static renderActiveFacets(
    html
  ) {
    const selectors = [
      '.active-facets-mobile',
      '.active-facets-desktop',
    ];

    selectors.forEach(
      (selector) => {
        const source =
          html.querySelector(
            selector
          );

        const target =
          document.querySelector(
            selector
          );

        if (
          source &&
          target
        ) {
          target.innerHTML =
            source.innerHTML;
        }
      }
    );

    FacetFiltersForm.toggleActiveFacets(
      false
    );
  }

  static renderAdditionalElements(
    html
  ) {
    const selectors = [
      '.mobile-facets__open',
      '.mobile-facets__count',
      '.sorting',
    ];

    selectors.forEach(
      (selector) => {
        const source =
          html.querySelector(
            selector
          );

        const target =
          document.querySelector(
            selector
          );

        if (
          source &&
          target
        ) {
          target.innerHTML =
            source.innerHTML;
        }
      }
    );

    FacetFiltersForm.initializeMobileFacets();
    FacetFiltersForm.ensureMobileCategory();
  }

  static renderCounts(
    source,
    target
  ) {
    if (!target) {
      return;
    }

    const targetSummary =
      target.querySelector(
        '.facets__summary'
      );

    const sourceSummary =
      source.querySelector(
        '.facets__summary'
      );

    if (
      targetSummary &&
      sourceSummary
    ) {
      targetSummary.outerHTML =
        sourceSummary.outerHTML;
    }

    const targetHeaderElement =
      target.querySelector(
        '.facets__header'
      );

    const sourceHeaderElement =
      source.querySelector(
        '.facets__header'
      );

    if (
      targetHeaderElement &&
      sourceHeaderElement
    ) {
      targetHeaderElement.outerHTML =
        sourceHeaderElement.outerHTML;
    }

    const targetWrapElement =
      target.querySelector(
        '.facets-wrap'
      );

    const sourceWrapElement =
      source.querySelector(
        '.facets-wrap'
      );

    if (
      targetWrapElement &&
      sourceWrapElement
    ) {
      targetWrapElement.outerHTML =
        sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(
    source,
    target
  ) {
    if (!target) {
      return;
    }

    const targetList =
      target.querySelector(
        '.mobile-facets__list'
      );

    const sourceList =
      source.querySelector(
        '.mobile-facets__list'
      );

    if (
      targetList &&
      sourceList
    ) {
      targetList.outerHTML =
        sourceList.outerHTML;
    }

    FacetFiltersForm.ensureMobileCategory();
  }

  static updateURLHash(
    searchParams
  ) {
    history.pushState(
      {
        searchParams,
      },
      '',
      `${window.location.pathname}${
        searchParams
          ? '?' + searchParams
          : ''
      }`
    );
  }

  static getSections() {
    const productGrid =
      document.getElementById(
        'product-grid'
      );

    if (!productGrid) {
      return [];
    }

    return [
      {
        section:
          productGrid.dataset.id,
      },
    ];
  }

  createSearchParams(
    form
  ) {
    const formData =
      new FormData(form);

    return new URLSearchParams(
      formData
    ).toString();
  }

  onSubmitForm(
    searchParams,
    event
  ) {
    FacetFiltersForm.renderPage(
      searchParams,
      event
    );
  }

  onSubmitHandler(
    event
  ) {
    event.preventDefault();

    const sortFilterForms =
      document.querySelectorAll(
        'facet-filters-form form'
      );

    if (
      event.target.classList.contains(
        'mobile-facets__checkbox'
      )
    ) {
      const form =
        event.target.closest(
          'form'
        );

      if (!form) {
        return;
      }

      const searchParams =
        this.createSearchParams(
          form
        );

      this.onSubmitForm(
        searchParams,
        event
      );

      return;
    }

    const currentForm =
      event.target.closest(
        'form'
      );

    if (!currentForm) {
      return;
    }

    const forms = [];

    const isMobile =
      currentForm.id ===
      'FacetFiltersFormMobile';

    sortFilterForms.forEach(
      (form) => {
        if (!isMobile) {
          if (
            form.id ===
              'FacetSortForm' ||
            form.id ===
              'FacetFiltersForm' ||
            form.id ===
              'FacetSortDrawerForm'
          ) {
            forms.push(
              this.createSearchParams(
                form
              )
            );
          }
        } else if (
          form.id ===
          'FacetFiltersFormMobile'
        ) {
          forms.push(
            this.createSearchParams(
              form
            )
          );
        }
      }
    );

    this.onSubmitForm(
      forms.join('&'),
      event
    );
  }

  onActiveFilterClick(
    event
  ) {
    event.preventDefault();

    FacetFiltersForm.toggleActiveFacets();

    const url =
      event.currentTarget.href.indexOf(
        '?'
      ) === -1
        ? ''
        : event.currentTarget.href.slice(
            event.currentTarget.href.indexOf(
              '?'
            ) + 1
          );

    FacetFiltersForm.renderPage(
      url
    );
  }
}


FacetFiltersForm.filterData = [];

FacetFiltersForm.searchParamsInitial =
  window.location.search.slice(1);

FacetFiltersForm.searchParamsPrev =
  window.location.search.slice(1);


customElements.define(
  'facet-filters-form',
  FacetFiltersForm
);

FacetFiltersForm.setListeners();


class PriceRange extends HTMLElement {
  constructor() {
    super();

    this.querySelectorAll(
      'input'
    ).forEach(
      (element) => {
        element.addEventListener(
          'change',
          this.onRangeChange.bind(
            this
          )
        );

        element.addEventListener(
          'keydown',
          this.onKeyDown.bind(
            this
          )
        );
      }
    );

    this.setMinAndMaxValues();
  }

  onRangeChange(
    event
  ) {
    this.adjustToValidValues(
      event.currentTarget
    );

    this.setMinAndMaxValues();
  }

  onKeyDown(
    event
  ) {
    if (event.metaKey) {
      return;
    }

    const pattern =
      /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;

    if (
      !event.key.match(
        pattern
      )
    ) {
      event.preventDefault();
    }
  }

  setMinAndMaxValues() {
    const inputs =
      this.querySelectorAll(
        'input'
      );

    const minInput =
      inputs[0];

    const maxInput =
      inputs[1];

    if (
      !minInput ||
      !maxInput
    ) {
      return;
    }

    if (maxInput.value) {
      minInput.setAttribute(
        'data-max',
        maxInput.value
      );
    }

    if (minInput.value) {
      maxInput.setAttribute(
        'data-min',
        minInput.value
      );
    }

    if (
      minInput.value === ''
    ) {
      maxInput.setAttribute(
        'data-min',
        0
      );
    }
  }

  adjustToValidValues(
    input
  ) {
    const value =
      Number(input.value);

    const min =
      Number(
        input.getAttribute(
          'data-min'
        )
      );

    const max =
      Number(
        input.getAttribute(
          'data-max'
        )
      );

    if (value < min) {
      input.value = min;
    }

    if (value > max) {
      input.value = max;
    }
  }
}


customElements.define(
  'price-range',
  PriceRange
);


class FacetRemove extends HTMLElement {
  constructor() {
    super();

    const facetLink =
      this.querySelector('a');

    if (!facetLink) {
      return;
    }

    facetLink.setAttribute(
      'role',
      'button'
    );

    facetLink.addEventListener(
      'click',
      this.closeFilter.bind(this)
    );

    facetLink.addEventListener(
      'keyup',
      (event) => {
        event.preventDefault();

        if (
          event.code.toUpperCase() ===
          'SPACE'
        ) {
          this.closeFilter(
            event
          );
        }
      }
    );
  }

  closeFilter(
    event
  ) {
    event.preventDefault();

    const form =
      this.closest(
        'facet-filters-form'
      ) ||
      document.querySelector(
        'facet-filters-form'
      );

    if (form) {
      form.onActiveFilterClick(
        event
      );
    }
  }
}


customElements.define(
  'facet-remove',
  FacetRemove
);